import React, { useMemo } from 'react';
import { useSurvey } from '../../../../admin-page/apis/useSurvey';
import SurveyForm from '../../../../SurveyForm';
import { useQueryClient } from '@tanstack/react-query';
import { ADMIN_PROGRESS_QUERY_KEY } from '../../../useAdminProgressQuery';

const addChooseNotToAnswer = (options) => [
  ...options,
  {
    value: 'choose_not_to_answer',
    label: 'I choose not to answer',
  },
];

const ezTrainingLabel = (
  <>
    <i>ez</i>Training
  </>
);

const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value;

export default function PreTrainingSurvey({ closeModal }) {
  const queryClient = useQueryClient();
  const mutation = useSurvey({
    url: '/api/survey/adminpre',
  });

  const questions = useMemo(
    () => [
      {
        name: 'zip',
        type: 'zip',
        label: 'What is your ZIP code?',
        hint: '5 digits',
        placeholder: 'e.g., 80202',
      },
      {
        name: 'education',
        type: 'single',
        label: 'What is your highest level of education completed?',
        required: true,
        options: addChooseNotToAnswer([
          {
            value: 'high_school_diploma_or_equivalent',
            label: 'High school diploma or equivalent',
          },
          { value: 'some_college', label: 'Some college' },
          { value: 'associate_degree', label: 'Associate degree' },
          { value: 'bachelors_degree', label: "Bachelor's degree" },
          { value: 'masters_degree', label: "Master's degree" },
          { value: 'doctoral_degree', label: 'Doctoral degree' },
        ]),
      },
      {
        name: 'sector',
        type: 'single',
        label: 'In what sector do you primarily work:',
        required: true,
        options: addChooseNotToAnswer([
          { value: 'mental_health', label: 'Mental Health' },
          { value: 'school_early_childhood', label: 'School/Early Childhood' },
          {
            value: 'primary_care_setting',
            label: 'Primary Care Setting',
          },
          {
            value: 'university_academic_setting',
            label: 'University/Academic Setting',
          },
          { value: 'community', label: 'Community' },
          { value: 'other', label: 'Other (please specify)' },
        ]),
      },
      {
        name: 'sectorSpecify',
        type: 'text',
        numbered: false,
        label: 'If you selected Other for sector, please specify:',
        placeholder: 'Type your response',
        showWhen: (values) => values.sector === 'other',
      },
      {
        name: 'familyRoles',
        type: 'multi',
        label:
          'What types of roles or activities have you had with families of young children? (Select all that apply):',
        required: true,
        options: [
          {
            value: 'parenting_education_or_parenting_skills_group',
            label: 'Parenting education or parenting skills group',
          },
          { value: 'home_visiting', label: 'Home visiting' },
          {
            value: 'early_childhood_education',
            label: 'Early childhood education (e.g., preschool, Head Start)',
          },
          {
            value: 'behavioral_or_mental_health_services',
            label: 'Behavioral or mental health services',
          },
          {
            value: 'case_management_or_family_support_services',
            label: 'Case management or family support services',
          },
          { value: 'other', label: 'Other (please specify)' },
          {
            value: 'choose_not_to_answer',
            label: 'I choose not to answer',
          },
        ],
      },
      {
        name: 'familyRolesSpecify',
        type: 'text',
        numbered: false,
        label: 'If you selected Other for roles or activities, please specify:',
        placeholder: 'Type your response',
        showWhen: (values) => Array.isArray(values.familyRoles)
          && values.familyRoles.includes('other'),
      },
      {
        name: 'groupExperience',
        type: 'single',
        label:
          'How much experience do you have in facilitating groups with families of young children?',
        required: true,
        options: addChooseNotToAnswer([
          { value: 'none', label: 'None' },
          { value: 'a_little', label: 'A Little' },
          { value: 'some', label: 'Some' },
          { value: 'quite_a_bit', label: 'Quite a Bit' },
        ]),
      },
      {
        name: 'confidence',
        type: 'single',
        label:
          'How confident do you feel about facilitating a parenting skills group?',
        required: true,
        options: addChooseNotToAnswer([
          { value: 'not_at_all_confident', label: 'Not at all confident' },
          { value: 'slightly_confident', label: 'Slightly confident' },
          { value: 'moderately_confident', label: 'Moderately confident' },
          { value: 'very_confident', label: 'Very confident' },
        ]),
      },
    ],
    [],
  );

  const handleSubmit = async (responses) => {
    const getQuestionOptions = (questionName) =>
      questions.find((question) => question.name === questionName)?.options ??
      [];

    const payload = {
      zip: responses.zip,
      education: responses.education
        ? getOptionLabel(
            getQuestionOptions('education'),
            responses.education,
          )
        : null,
      sector: responses.sector
        ? getOptionLabel(getQuestionOptions('sector'), responses.sector)
        : null,
      sectorSpecify:
        responses.sector === 'other' ? responses.sectorSpecify : null,
      familyRoles: Array.isArray(responses.familyRoles)
        ? responses.familyRoles.map((role) =>
            getOptionLabel(getQuestionOptions('familyRoles'), role),
          )
        : [],
      familyRolesSpecify:
        Array.isArray(responses.familyRoles)
        && responses.familyRoles.includes('other')
          ? responses.familyRolesSpecify
          : null,
      groupExperience: responses.groupExperience
        ? getOptionLabel(
            getQuestionOptions('groupExperience'),
            responses.groupExperience,
          )
        : null,
      confidence: responses.confidence
        ? getOptionLabel(
            getQuestionOptions('confidence'),
            responses.confidence,
          )
        : null,
    };

    await mutation.mutateAsync(payload);
    await queryClient.invalidateQueries({
      queryKey: ADMIN_PROGRESS_QUERY_KEY,
    });
    closeModal();
  };

  return (
    <SurveyForm
      title={<>Pre {ezTrainingLabel} Survey</>}
      description={
        <>
          Please complete the following questions before starting the{' '}
          {ezTrainingLabel} modules. This information helps us understand{' '}
          {ezTrainingLabel} participation.
        </>
      }
      questions={questions}
      onSubmit={handleSubmit}
      defaultValues={{
        zip: '',
        education: '',
        sector: '',
        sectorSpecify: '',
        familyRoles: [],
        familyRolesSpecify: '',
        groupExperience: '',
        confidence: '',
      }}
      submitLabel={
        <span>
          <span>Start</span>
          <span className='ml-1'>{ezTrainingLabel}</span>
        </span>
      }
    />
  );
}
